<template>
  <div
    class="autocomplete"
    @keydown.down.prevent="move(1)"
    @keydown.up.prevent="move(-1)"
    @keydown.enter.prevent="choose(active)"
    @keydown.esc="close"
  >
    <input
      ref="input"
      type="text"
      class="formulate-input-element formulate-input-element--text w-full"
      :value="value"
      :placeholder="placeholder"
      autocomplete="off"
      @input="onInput($event.target.value)"
      @focus="open = items.length > 0"
      @blur="onBlur"
    />
    <ul v-if="open && (items.length || loading)" class="autocomplete-list">
      <li v-if="loading" class="autocomplete-item text-gray-400">Searching…</li>
      <li
        v-for="(item, i) in items"
        :key="i"
        class="autocomplete-item"
        :class="{ 'autocomplete-item--active': i === active }"
        @mousedown.prevent="choose(i)"
        @mouseenter="active = i"
      >
        {{ item.label }}
      </li>
      <li
        v-if="!loading && !items.length && query"
        class="autocomplete-item text-gray-400"
      >
        No matches
      </li>
    </ul>
  </div>
</template>

<script>
// Reusable typeahead: emits `search(query)` (debounced) so the parent can fetch,
// and `select(item)` when the user picks one. The parent passes back `items`
// ([{ label, value, raw }]) and `loading`. Display value is controlled via `value`.
export default {
  name: 'AutocompleteField',
  props: {
    value: { type: String, default: '' },
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    placeholder: { type: String, default: '' },
    minChars: { type: Number, default: 2 },
    debounce: { type: Number, default: 250 },
  },
  data() {
    return { open: false, active: -1, query: this.value, timer: null }
  },
  watch: {
    items() {
      this.open = this.items.length > 0 || (!this.loading && !!this.query)
      this.active = -1
    },
  },
  methods: {
    onInput(v) {
      this.query = v
      this.$emit('input', v) // keep the raw typed text as the field value
      clearTimeout(this.timer)
      if (v.length < this.minChars) {
        this.$emit('items-clear')
        this.open = false
        return
      }
      this.timer = setTimeout(() => this.$emit('search', v), this.debounce)
    },
    move(d) {
      if (!this.open || !this.items.length) return
      this.active = (this.active + d + this.items.length) % this.items.length
    },
    choose(i) {
      const item = this.items[i]
      if (!item) return
      this.$emit('select', item)
      this.close()
    },
    onBlur() {
      // Delay so a mousedown on an item still registers.
      setTimeout(() => this.close(), 150)
    },
    close() {
      this.open = false
      this.active = -1
    },
  },
}
</script>

<style lang="scss" scoped>
.autocomplete {
  position: relative;
}
.autocomplete-list {
  position: absolute;
  z-index: 30;
  left: 0;
  right: 0;
  max-height: 16rem;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
}
.autocomplete-item {
  padding: 0.45rem 0.75rem;
  cursor: pointer;
  font-size: 0.875rem;
}
.autocomplete-item--active {
  background: #f3e8ff;
}
</style>
